import React, {
  FunctionComponent,
  JSXElementConstructor,
  CSSProperties,
} from 'react'
import cn from 'clsx'
import s from './Text.module.scss'
import parse, {
  HTMLReactParserOptions,
  Element,
  domToReact,
} from 'html-react-parser'
import Image from 'next/image'

interface TextProps {
  variant?: Variant
  className?: string
  style?: CSSProperties
  children?: React.ReactNode | any
  html?: string
  onClick?: () => any
  component?: string
}

type Variant =
  | 'heading'
  | 'headingLarge'
  | 'body'
  | 'pageHeading'
  | 'sectionHeading'
  | 'sectionHeading2'
  | 'sectionHeadingV2'
  | 'smallText'
  | 'smallTextSmall'
  | 'contentHeading'
  | 'text-3'
  | 'span'

const Text: FunctionComponent<TextProps> = ({
  style,
  className = '',
  variant = 'body',
  children,
  html,
  onClick,
  component,
}) => {
  const componentsMap: {
    [P in Variant]: React.ComponentType<any> | string
  } = {
    body: 'div',
    heading: 'h1',
    headingLarge: 'h2',
    pageHeading: 'h1',
    sectionHeading: 'h2',
    sectionHeading2: 'h2',
    sectionHeadingV2: 'h2',
    contentHeading: 'h3',
    smallText: 'div',
    smallTextSmall: 'div',
    'text-3': 'div',
    span: 'span',
  }

  const Component:
    | JSXElementConstructor<any>
    | React.ReactElement<any>
    | React.ComponentType<any>
    | string = component || componentsMap![variant!]

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.name === 'img') {
        const { src, alt, width, height, style: imgStyle } = domNode.attribs

        if (src && (src.startsWith('/media/') || src.startsWith('media/'))) {
          const host = 'https://lore.cc'
          const absoluteSrc = src.startsWith('/')
            ? `${host}${src}`
            : `${host}/${src}`

          // Try to get dimensions from attributes, otherwise fallback
          const w = width ? parseInt(width) : undefined
          const h = height ? parseInt(height) : undefined

          if (w && h) {
            return (
              <div className={'text-image'}>
                <Image
                  src={absoluteSrc}
                  alt={alt || ''}
                  width={w}
                  height={h}
                  layout="responsive"
                  objectFit="contain"
                />
              </div>
            )
          }

          return (
            <span
              style={{
                display: 'block',
                position: 'relative',
                width: '100%',
                height: 'auto',
                aspectRatio: '16/9', // Rough fallback
              }}
              className={'text-image'}
            >
              <Image
                src={absoluteSrc}
                alt={alt || ''}
                layout="fill"
                objectFit="contain"
              />
            </span>
          )
        }
      }
    },
  }

  const content = html ? parse(html, options) : children

  return (
    <Component
      className={cn(
        s.root,
        {
          [s.heading]: variant === 'heading',
          [s.headingLarge]: variant === 'headingLarge',
          [s.pageHeading]: variant === 'pageHeading',
          [s.sectionHeading]: variant === 'sectionHeading',
          [s.sectionHeading2]: variant === 'sectionHeading2',
          [s.sectionHeadingV2]: variant === 'sectionHeadingV2',
          [s.contentHeading]: variant === 'contentHeading',
          [s.smallText]: variant === 'smallText',
          [s.smallTextSmall]: variant === 'smallTextSmall',
          [s.text3]: variant === 'text-3',
        },
        className
      )}
      onClick={onClick}
      style={style}
    >
      {content}
    </Component>
  )
}

export default Text
